import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import { ResourceListShell } from '../../components/resource-views/resource-list-shell';
import { useListResourceTypesQuery } from '../../store/hooks';
import {
  applyFilters,
  readFilters,
  type ListFilters,
} from '../../utils/resource-list-params';
import {
  parseResourceScope,
  resourcesRootPath,
} from '../../utils/resource-routes';

const DEFAULT_PER_PAGE = 25;

/**
 * Owner-scoped list for one resource type (GDN-2723).
 *
 * List state lives in the URL rather than component state, so a filtered,
 * sorted, paged list is the thing you can hand to someone else — the bespoke
 * lists kept all of this in `useState` and lost it on every navigation.
 */
export default function ResourceListPage() {
  const params = useParams<{
    organizationId?: string;
    userId?: string;
    resourceType?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scope = parseResourceScope(params);
  const resourceType = params.resourceType;

  const { data: catalog } = useListResourceTypesQuery();
  const entry = catalog?.items?.find(r => r.resource_type === resourceType);

  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const perPage =
    Number(searchParams.get('per_page') ?? '') || DEFAULT_PER_PAGE;
  const sortBy = searchParams.get('sort_by') ?? undefined;
  const sortOrder =
    (searchParams.get('sort_order') as 'asc' | 'desc' | null) ?? undefined;
  const filters = readFilters(searchParams);

  // Both handlers use the functional updater so they don't close over
  // `searchParams`. A handler whose identity changed on every navigation
  // re-triggered the table's filter effect, which reset the page (GDN-2732).
  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const onFiltersChange = useCallback(
    (nextFilters: ListFilters) => {
      setSearchParams(prev => applyFilters(prev, nextFilters) ?? prev);
    },
    [setSearchParams],
  );

  if (!scope || !resourceType) return null;

  return (
    <Box>
      <Button
        onClick={() => navigate(resourcesRootPath(scope))}
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
      >
        All resources
      </Button>
      {entry && entry.capability === null ? (
        <Typography color="text.secondary">
          {resourceType} has no generic list on the common interface yet. It can
          still be opened by id from a detail link.
        </Typography>
      ) : (
        <ResourceListShell
          resourceType={resourceType}
          scope={scope}
          page={page}
          perPage={perPage}
          filters={filters}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={p => patch({ page: String(p) })}
          onPerPageChange={n => patch({ per_page: String(n), page: null })}
          onFiltersChange={onFiltersChange}
          onSortChange={(by, order) =>
            patch({ sort_by: by, sort_order: order, page: null })
          }
        />
      )}
    </Box>
  );
}
